import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate, Outlet } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  CalendarClock,
  MapPinned,
  Wrench,
  Users,
  Package,
  FileText,
  ClipboardList,
  UserCog,
  Settings,
  LogOut,
  Plus,
  ChevronDown,
  ChevronLeft,
  Search,
  Moon,
  Sun,
  ListTodo,
  UserRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/lib/theme";
import { initials } from "@/lib/format";
import { useTaskSummary } from "@/lib/tasks";
import { Logo, BrandMark } from "./Logo";
import { GlobalSearch } from "./GlobalSearch";
import { MobileBottomNav } from "./mobile/MobileBottomNav";
import { NewReportDrawer } from "./mobile/NewReportDrawer";
import { MoreDrawer } from "./mobile/MoreDrawer";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "sidebar-nav-dashboard", end: true },
  { to: "/kalender", label: "Kalender", icon: CalendarClock, testid: "sidebar-nav-kalender" },
  { to: "/kart", label: "Kart", icon: MapPinned, testid: "sidebar-nav-kart", feature: "map_enabled" },
  { to: "/maskiner", label: "Maskiner", icon: Wrench, testid: "sidebar-nav-maskiner" },
  { to: "/kunder", label: "Kundar", icon: Users, testid: "sidebar-nav-kunder" },
  { to: "/deler", label: "Oljer & filter", icon: Package, testid: "sidebar-nav-deler", feature: "parts_page_enabled" },
  { to: "/rapportar", label: "Rapportar", icon: FileText, testid: "sidebar-nav-rapporter" },
  { to: "/huskeliste", label: "Huskeliste", icon: ListTodo, testid: "sidebar-nav-huskeliste", badge: "tasks" },
];

/** Nav items whose `feature` flag is off are hidden (module built, but not switched on in pilot). */
export const visibleNav = (items, features) => items.filter((i) => !i.feature || features?.[i.feature]);

const PAGE_TITLES = {
  "/": "Dashboard",
  "/kalender": "Kalender",
  "/kart": "Kart",
  "/maskiner": "Maskiner",
  "/kunder": "Kundar",
  "/deler": "Oljer & filter",
  "/rapportar": "Rapportar",
  "/rapportar/ny-service": "Ny servicerapport",
  "/rapportar/ny-jobb": "Ny jobbrapport",
  "/huskeliste": "Huskeliste",
  "/profil": "Min konto",
  "/brukarar": "Brukarar",
  "/innstillingar": "Innstillingar",
};

/** Where the mobile topbar back-arrow should lead (sub pages only). */
const BACK_TARGET = (path) => {
  if (path.startsWith("/maskiner/")) return "/maskiner";
  if (path.startsWith("/rapportar/ny")) return "/rapportar";
  if (path === "/profil") return "/";
  return null;
};

const navClass = ({ isActive }) =>
  cn(
    "group flex h-11 items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors duration-150",
    isActive
      ? "bg-white/10 text-white shadow-[inset_2px_0_0_0_#FF4E00]"
      : "text-white/70 hover:bg-white/5 hover:text-white"
  );

const SidebarContent = ({ isAdmin, user, onNavigate, features, badges }) => (
  <div className="flex h-full flex-col bg-[#0B0D10] text-white">
    <div className="flex h-16 items-center border-b border-white/10 px-5">
      <NavLink to="/" onClick={onNavigate} className="flex items-center" data-testid="sidebar-logo-link">
        <Logo onDark height={30} />
      </NavLink>
    </div>
    <nav className="sidebar-scroll flex-1 space-y-1 overflow-y-auto px-3 py-4">
      {visibleNav(NAV, features).map((item) => {
        const Icon = item.icon;
        const badge = item.badge ? badges?.[item.badge] : null;
        return (
          <NavLink key={item.to} to={item.to} end={item.end} data-testid={item.testid} onClick={onNavigate} className={navClass}>
            <Icon className="h-5 w-5" />
            <span className="flex-1">{item.label}</span>
            {badge?.count > 0 && (
              <span
                className={cn("rounded-full px-1.5 py-0.5 text-[11px] font-bold tabular-nums leading-none", badge.urgent ? "bg-primary text-[#0B0D10]" : "bg-white/15 text-white")}
                data-testid={`${item.testid}-badge`}
                aria-label={`${badge.count} opne punkt`}
              >
                {badge.count}
              </span>
            )}
          </NavLink>
        );
      })}
      {isAdmin && (
        <>
          <div className="px-3 pb-1 pt-5 text-[11px] font-semibold uppercase tracking-wider text-white/40">
            Administrasjon
          </div>
          <NavLink to="/brukarar" data-testid="sidebar-nav-brukarar" onClick={onNavigate} className={navClass}>
            <UserCog className="h-5 w-5" />
            Brukarar
          </NavLink>
          <NavLink to="/innstillingar" data-testid="sidebar-nav-innstillingar" onClick={onNavigate} className={navClass}>
            <Settings className="h-5 w-5" />
            Innstillingar
          </NavLink>
        </>
      )}
    </nav>
    <div className="border-t border-white/10 p-4">
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-xs font-bold text-[#0B0D10]">
          {initials(user?.name)}
        </span>
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="truncate text-[11px] text-white/50">{user?.title || (user?.role === "admin" ? "Administrator" : "Mekaniker")}</div>
        </div>
      </div>
    </div>
  </div>
);

const NewReportMenu = ({ trigger, align = "end" }) => {
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-64 p-1.5" data-testid="new-report-dropdown">
        <DropdownMenuItem
          data-testid="new-report-service"
          className="cursor-pointer gap-3 rounded-lg py-3"
          onClick={() => navigate("/rapportar/ny-service")}
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted"><FileText className="h-4 w-4" /></span>
          <div>
            <div className="font-semibold">Servicerapport</div>
            <div className="text-xs text-muted-foreground">Oljeskift, filter, årskontroll</div>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid="new-report-jobb"
          className="cursor-pointer gap-3 rounded-lg py-3"
          onClick={() => navigate("/rapportar/ny-jobb")}
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted"><ClipboardList className="h-4 w-4" /></span>
          <div>
            <div className="font-semibold">Jobbrapport</div>
            <div className="text-xs text-muted-foreground">Reparasjon eller anna jobb</div>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default function AppShell() {
  const { user, logout, isAdmin, features } = useAuth();
  const { isDark, toggle } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const taskSummary = useTaskSummary();
  const badges = { tasks: { count: taskSummary?.open || 0, urgent: (taskSummary?.overdue || 0) > 0 } };

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Close mobile drawers on navigation
  useEffect(() => {
    setNewOpen(false);
    setMoreOpen(false);
  }, [location.pathname]);

  const path = location.pathname;
  const title =
    PAGE_TITLES[path] ||
    (path.startsWith("/maskiner/") ? "Maskin" : "") ||
    "Vest Maskinservice";
  const isFormPage = path.startsWith("/rapportar/ny");
  const backTo = BACK_TARGET(path);
  const showBottomNav = !isFormPage;

  return (
    <div className="min-h-[100dvh] bg-background">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-[264px] lg:block" data-testid="sidebar">
        <SidebarContent isAdmin={isAdmin} user={user} features={features} badges={badges} />
      </aside>

      <div className="lg:pl-[264px]">
        {/* Topbar */}
        <header
          data-testid="topbar"
          className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-border bg-background/85 px-3 pt-[var(--safe-top)] backdrop-blur-md supports-[backdrop-filter]:bg-background/70 sm:px-6 md:h-16"
        >
          {/* Mobile: back arrow on sub pages, brand mark elsewhere */}
          {backTo ? (
            <Button variant="ghost" size="icon" className="-ml-1 lg:hidden" data-testid="topbar-back-button" onClick={() => navigate(backTo)} aria-label="Tilbake">
              <ChevronLeft className="h-6 w-6" />
            </Button>
          ) : (
            <NavLink to="/" className="mr-1 flex h-11 items-center lg:hidden" aria-label="Til dashboard" data-testid="topbar-brand-link">
              <BrandMark size={22} />
            </NavLink>
          )}
          <h1 className="truncate font-heading text-base font-bold text-foreground sm:text-lg">{title}</h1>

          <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
            {/* Search: field on desktop, icon on mobile */}
            <button
              type="button"
              data-testid="global-search-trigger"
              onClick={() => setSearchOpen(true)}
              className="hidden h-10 w-64 items-center gap-2 rounded-lg border border-input bg-card px-3 text-sm text-muted-foreground shadow-sm transition-colors hover:bg-accent md:flex"
            >
              <Search className="h-4 w-4" />
              <span className="flex-1 text-left">Søk maskin, kunde …</span>
              <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-semibold">Ctrl K</kbd>
            </button>
            <Button variant="ghost" size="icon" className="md:hidden" data-testid="global-search-trigger-mobile" onClick={() => setSearchOpen(true)} aria-label="Søk">
              <Search className="h-5 w-5" />
            </Button>

            <NewReportMenu
              trigger={
                <Button className="hidden md:inline-flex" data-testid="topbar-new-report-button">
                  <Plus /> Ny rapport <ChevronDown className="-mr-1 h-4 w-4 opacity-70" />
                </Button>
              }
            />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="topbar-user-menu"
                  className="flex h-11 items-center gap-2 rounded-lg px-1.5 transition-colors hover:bg-accent sm:px-2"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0B0D10] text-xs font-bold text-white dark:bg-white dark:text-[#0B0D10]">
                    {initials(user?.name)}
                  </span>
                  <span className="hidden text-left md:block">
                    <span className="block text-sm font-semibold leading-tight text-foreground">{user?.name}</span>
                    <span className="block text-[11px] leading-tight text-muted-foreground">{user?.title || (user?.role === "admin" ? "Administrator" : "Mekaniker")}</span>
                  </span>
                  <ChevronDown className="hidden h-4 w-4 text-muted-foreground md:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuLabel>
                  <div className="font-semibold">{user?.name}</div>
                  <div className="text-xs font-normal text-muted-foreground">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="user-menu-profile" onClick={() => navigate("/profil")} className="cursor-pointer">
                  <UserRound className="mr-2 h-4 w-4" /> Min konto
                  <span className="ml-auto text-[11px] text-muted-foreground">Outlook</span>
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="theme-toggle" onClick={toggle} className="cursor-pointer">
                  {isDark ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
                  {isDark ? "Lys modus" : "Mørk modus"}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="logout-button"
                  onClick={() => {
                    logout();
                    navigate("/login");
                  }}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" /> Logg ut
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main
          className={cn(
            "mx-auto w-full max-w-7xl px-4 py-5 sm:px-6 sm:py-6 lg:px-8 lg:pb-10",
            showBottomNav ? "pb-[calc(var(--mobile-nav-total)+28px)]" : "pb-0 sm:pb-6"
          )}
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      {/* Mobile v2: bottom navigation + drawers (hidden on form pages, where the sticky save bar takes over) */}
      {showBottomNav && <MobileBottomNav onNew={() => setNewOpen(true)} onMore={() => setMoreOpen(true)} moreActive={moreOpen} />}
      <NewReportDrawer open={newOpen} onOpenChange={setNewOpen} />
      <MoreDrawer open={moreOpen} onOpenChange={setMoreOpen} />

      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  );
}
